import React, { ReactNode } from 'react';
import { Box, IBoxProps, Skeleton } from 'native-base';
import { faHeart, faStar } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartOutline } from '@fortawesome/free-regular-svg-icons';
import Image from './Image';
import { SpIcon } from './Icon';
import { Caption } from './Typography';
import { resizedUrl, TypographyProps, useTheme } from '@springbig/shared';
import { Button, Card } from '~/shared/Theme';

export type ItemCardProps = {
    handleCallback: (id: number, title?: string, interest?: string) => void;
    id?: number;
    title?: string;
    imageUrl?: string;
    boxProps?: IBoxProps;
    checked?: boolean;
    needsMark?: boolean;
    isDisabled?: boolean;
    interest?: string;
    customImageWidth?: number;
    customImageWidthPx?: string;
    captionStyle?: TypographyProps;
};

type ItemCardStyledContainerProps = {
    children: ReactNode;
    boxProps?: IBoxProps;
};

const ItemCardStyledContainer = ({
    children,
    boxProps,
}: ItemCardStyledContainerProps) => {

    const {
        field_background,
        field_border
    } = useTheme();

    return (
        <Box
            {...boxProps}
            bg={field_background}
            borderColor={field_border}
            borderWidth={0.25}
            borderTopRadius={12}
            borderBottomRadius={12}
            p={1}
        >
            { children }
        </Box>
    );
};

const ItemCard = ({
    handleCallback,
    id,
    title,
    imageUrl,
    checked,
    isDisabled,
    interest,
    customImageWidth,
    needsMark = false,
    captionStyle,
    customImageWidthPx
}: ItemCardProps) => {

    const { primary } = useTheme();

    return (
        <Button
            modifier={'unlocked'}
            variant={'text'}
            onPress={() => {

                handleCallback(id!, title, interest);
            }}
            key={id}
            disabled={isDisabled}
            loading={isDisabled}
        >
            <Card
                w={customImageWidth || 40}
                position={'relative'}
                p={0}
                secondary
            >
                { imageUrl ?
                    <img 
                        style={{ width: customImageWidthPx || '168px', height: '168px', aspectRatio: 1 }}
                        src={resizedUrl(imageUrl, 192, 192)}
                    />
                    :
                    <Box
                        w={customImageWidth || 40}
                        h={40}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        <SpIcon
                            icon={faStar}
                            size={120}
                            color={primary}
                        />
                    </Box>
                }
                { needsMark &&
                        <Box position={'absolute'} top={4} right={4}>
                            {
                                checked ? (
                                    <SpIcon
                                        size={16}
                                        color={primary}
                                        icon={faHeart}
                                    />
                                ) : (
                                    <SpIcon
                                        size={16}
                                        color={primary}
                                        icon={faHeartOutline}
                                    />
                                )
                            }
                        </Box>
                }
                <Box
                    h={16}
                    justifyContent={'center'}
                    alignItems={'center'}
                >
                    <Caption
                        maxW={'90%'}
                        textAlign={'center'}
                        numberOfLines={2}
                        ellipsizeMode={'tail'}
                        {...captionStyle}
                    >
                        { title }
                    </Caption>
                </Box>
            </Card>
        </Button>
    );
};

export const ItemCardSkeleton = () => {

    return (
        <ItemCardStyledContainer  boxProps={{ alignItems: 'center', p: 4 , m: 4, borderRadius: 5 }}>
            <Skeleton w={140} h={140} isLoaded={false}  borderTopRadius={10}/>
            <Skeleton.Text lines={1} px="4" isLoaded={false} paddingY={6} />
        </ItemCardStyledContainer>
    );
};

export default ItemCard;
